import { type ChangeEvent, type FormEvent, useId, useState } from "react";
import { postCoraTicket } from "@/api/cora";
import { GfgButton } from "@/components/ui/GfgButton";
import { CORA_SCREENSHOT_UI, CORA_UI } from "@/constants/cora";
import type { CoraHistoryLine, CoraQuestion, CoraTicketType } from "@/interfaces/cora";

export interface CoraTicketFormProps {
  conversationId: string;
  ticketType: CoraTicketType;
  questions: CoraQuestion[];
  prefilled: Record<string, string>;
  history: CoraHistoryLine[];
  screenKey: string | null;
  onSubmitted?: (filed: boolean) => void;
}

const SCREENSHOT_TYPES: readonly string[] = CORA_SCREENSHOT_UI.ACCEPT.split(",");

/** The ask_details outcome: one form answering Cora's questions, with an optional screenshot. */
export function CoraTicketForm({
  conversationId,
  ticketType,
  questions,
  prefilled,
  history,
  screenKey,
  onSubmitted,
}: CoraTicketFormProps) {
  const idPrefix = useId();
  const [answers, setAnswers] = useState<Record<string, string>>(() =>
    Object.fromEntries(questions.map((q) => [q.id, prefilled[q.id] ?? ""])),
  );
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const screenshotId = `${idPrefix}-screenshot`;
  const screenshotErrorId = `${idPrefix}-screenshot-error`;
  const errorId = `${idPrefix}-error`;
  const locked = pending || sent;

  function handleScreenshot(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    if (
      file &&
      (file.size > CORA_SCREENSHOT_UI.MAX_BYTES || !SCREENSHOT_TYPES.includes(file.type))
    ) {
      event.target.value = "";
      setScreenshot(null);
      setScreenshotError(CORA_UI.TICKET_SCREENSHOT_INVALID);
      return;
    }
    setScreenshot(file);
    setScreenshotError(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (locked) return;
    setPending(true);
    setError(null);
    try {
      const { filed } = await postCoraTicket({
        conversationId,
        ticketType,
        answers,
        labels: Object.fromEntries(questions.map((q) => [q.id, q.label])),
        history,
        screen: screenKey,
        articleSlug: null,
        screenshot,
      });
      setSent(true);
      onSubmitted?.(filed);
    } catch (err) {
      console.error("CoraTicketForm.submit", (err as { message?: string } | undefined)?.message);
      setError(CORA_UI.TICKET_FAILED);
    } finally {
      setPending(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      aria-describedby={error ? errorId : undefined}
      className="flex flex-col gap-3 text-[14px] text-rodeo-black"
    >
      <p className="m-0">{CORA_UI.TICKET_INTRO}</p>
      {questions.map((q) => {
        const fieldId = `${idPrefix}-${q.id}`;
        return (
          <div key={q.id} className="flex flex-col gap-1">
            <label htmlFor={fieldId} className="font-lato font-bold text-[13px] text-graphite">
              {q.label}
            </label>
            <textarea
              id={fieldId}
              value={answers[q.id] ?? ""}
              onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
              maxLength={CORA_UI.ANSWER_MAX_CHARS}
              disabled={locked}
              rows={2}
              className="w-full resize-y rounded-[6px] border border-smoke bg-white px-3 py-2 font-lato text-[14px] text-rodeo-black outline-none focus:border-dark-dust disabled:opacity-60"
            />
          </div>
        );
      })}
      <div className="flex flex-col gap-1">
        <label htmlFor={screenshotId} className="font-lato font-bold text-[13px] text-graphite">
          {CORA_UI.TICKET_SCREENSHOT_LABEL}
        </label>
        <input
          id={screenshotId}
          type="file"
          accept={CORA_SCREENSHOT_UI.ACCEPT}
          onChange={handleScreenshot}
          disabled={locked}
          aria-invalid={screenshotError ? true : undefined}
          aria-describedby={screenshotError ? screenshotErrorId : undefined}
          className="font-lato text-[13px] text-rodeo-black disabled:opacity-60"
        />
        {screenshotError && (
          <p id={screenshotErrorId} role="alert" className="m-0 text-[13px] text-good-wine">
            {screenshotError}
          </p>
        )}
      </div>
      {sent ? (
        <p role="status" className="m-0 text-[13px] text-graphite">
          {CORA_UI.TICKET_SENT}
        </p>
      ) : (
        <GfgButton type="submit" variant="primary" disabled={pending} className="self-end">
          {CORA_UI.TICKET_SUBMIT}
        </GfgButton>
      )}
      {error && (
        <p id={errorId} role="alert" className="m-0 text-[13px] text-good-wine">
          {error}
        </p>
      )}
    </form>
  );
}

export default CoraTicketForm;
